import { useState, useRef, useCallback } from 'react';

interface SwipeCallbacks {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
}

export function useSwipe({ onSwipeLeft, onSwipeRight, threshold = 60 }: SwipeCallbacks) {
  const [offset, setOffset] = useState(0);
  const [touchAction, setTouchAction] = useState<'pan-y' | 'none'>('pan-y');
  const startX = useRef(0);
  const startY = useRef(0);
  const isSwiping = useRef(false);
  const offsetRef = useRef(0);
  const hasMoved = useRef(false);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isSwiping.current = false;
    hasMoved.current = false;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;

    // Only start tracking if horizontal movement exceeds threshold
    if (!hasMoved.current && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
      hasMoved.current = true;
      isSwiping.current = true;
      setOffset(0);
      setTouchAction('none');  // Block all touch gestures during drag via CSS
      return;
    }

    if (!hasMoved.current) return;

    const currentDx = e.touches[0].clientX - startX.current;

    // Card follows finger direction
    if (currentDx < 0) {
      setOffset(Math.max(currentDx, -threshold));
      offsetRef.current = Math.max(currentDx, -threshold);
    } else if (currentDx > 0) {
      setOffset(Math.min(currentDx, threshold));
      offsetRef.current = Math.min(currentDx, threshold);
    }
  }, [threshold]);

  const onTouchEnd = useCallback(() => {
    if (!hasMoved.current) {
      setOffset(0);
      offsetRef.current = 0;
      setTouchAction('pan-y');
      return;
    }

    // Positive offset = card moved right = reveal left action
    // Negative offset = card moved left = reveal right action
    if (offsetRef.current >= threshold) {
      onSwipeLeft?.();
    } else if (offsetRef.current <= -threshold) {
      onSwipeRight?.();
    }
    // Always snap back to original position
    setOffset(0);
    offsetRef.current = 0;
    setTouchAction('pan-y');

    isSwiping.current = false;
    hasMoved.current = false;
  }, [threshold, onSwipeLeft, onSwipeRight]);

  const reset = useCallback(() => {
    setOffset(0);
    offsetRef.current = 0;
    setTouchAction('pan-y');
    isSwiping.current = false;
    hasMoved.current = false;
  }, []);

  return { offset, touchAction, onTouchStart, onTouchMove, onTouchEnd, reset };
}
